/**
 * Best-guess expense category from a vendor name. Used to pre-fill the
 * category dropdown after a receipt scan so the user lands on a
 * sensible default. Match is case-insensitive substring on a curated
 * map of common US vendors / chain names — falls back to whatever the
 * caller provides if no match.
 */

interface CategoryRule {
  match: RegExp;
  category: string;
}

// Categories used here must exist in EXPENSE_CATEGORIES (see
// src/lib/expenses.ts); the dropdown silently rejects unknowns.
const RULES: CategoryRule[] = [
  // Electric / gas / water utilities
  { match: /\b(dte|consumers energy|pge|pg&e|edison|duke|xcel|appalachian|conedison|coned|nationalgrid|national grid|seattle city light|austin energy|nv energy|fpl|firstenergy|comed|aps|water dept|municipal water|water district|wssc|dc water|sewer dist|sewer district)\b/i, category: 'Utilities' },

  // Internet / TV / phone
  { match: /\b(comcast|xfinity|spectrum|verizon|att|at&t|tmobile|t-mobile|google fiber|frontier|cox|centurylink|sonic|optimum|cricket|hulu|netflix)\b/i, category: 'Internet & cable' },

  // Trash / recycling
  { match: /\b(waste management|republic services|rumpke|recology|\bwm\b)\b/i, category: 'Trash & recycling' },

  // Mortgage
  { match: /\b(wells fargo|chase|rocket mortgage|quicken loans|loandepot|mr cooper|us bank|usbank|guild mortgage|caliber|fairway mortgage)\b/i, category: 'Mortgage' },

  // HOA
  { match: /\b(hoa|homeowners association|condo association|condominium association|community association)\b/i, category: 'HOA / condo fees' },

  // Insurance
  { match: /\b(state farm|geico|allstate|liberty mutual|progressive|farmers insurance|nationwide|usaa|travelers|aaa insurance|amica|metlife|lemonade)\b/i, category: 'Insurance' },

  // Property tax
  { match: /\b(county treasurer|property tax|assessor|tax collector|town of|township of)\b/i, category: 'Property tax' },

  // Pest control
  { match: /\b(orkin|terminix|ehrlich|truly nolen|aptive|pest|exterminator|rentokil|massey services)\b/i, category: 'Pest control' },

  // Landscaping / lawn
  { match: /\b(trugreen|lawn doctor|scotts lawn|landscap|mow|mowing|gardener|tree service|arbor|davey tree|bartlett tree)\b/i, category: 'Landscaping' },

  // Cleaning
  { match: /\b(merry maids|molly maid|the cleaning authority|cleaning service|housekeeper|maid service|stanley steemer)\b/i, category: 'Cleaning' },

  // Snow removal
  { match: /\b(snow plow|plowing|snow removal|snowblower service)\b/i, category: 'Snow removal' },

  // Pool service
  { match: /\b(pool service|pool guy|pool maintenance|leslie's pool|pinch a penny)\b/i, category: 'Pool service' },

  // Maintenance contractors
  { match: /\b(roto-rooter|mr rooter|servpro|servicemaster|hvac|heating|cooling|comfort pro|trane|carrier|lennox|rheem|plumb|plumber|electrician|electrical|chimney)\b/i, category: 'Maintenance' },

  // Improvements / big-box hardware / furniture
  { match: /\b(home depot|lowes|lowe's|menards|ace hardware|true value|harbor freight|tractor supply|orchard supply)\b/i, category: 'Improvements' },
  { match: /\b(best buy|bestbuy|wayfair|crate ?and ?barrel|ikea|west elm|cb2|article|raymour|ashley furniture)\b/i, category: 'Improvements' },
];

export function guessExpenseCategory(vendor: string | null): string | null {
  if (!vendor) return null;
  const v = vendor.trim();
  if (!v) return null;
  for (const rule of RULES) {
    if (rule.match.test(v)) return rule.category;
  }
  return null;
}
